import React from 'react';
import { Link } from 'react-router-dom';
import { Users, TrendingUp, MessageSquare, Check, ArrowRight } from 'lucide-react';

const Services = () => {
  const services = [
    {
      icon: <Users className="w-16 h-16" />,
      title: 'Referral Marketing',
      subtitle: 'Word-of-Mouth at Scale',
      description: 'Transform satisfied customers into powerful brand advocates with our comprehensive referral marketing solutions.',
      color: 'from-blue-500 to-cyan-500',
      features: [
        'Custom referral program design',
        'Automated reward management',
        'Tracking and analytics dashboard',
        'Integration with your existing systems',
        'Social sharing optimization',
        'Performance-based incentives'
      ]
    },
    {
      icon: <TrendingUp className="w-16 h-16" />,
      title: 'TikTok Video Boosting',
      subtitle: 'Go Viral, Gain Visibility',
      description: 'Amplify your TikTok presence with strategic video boosting campaigns designed to maximize reach and engagement.',
      color: 'from-purple-500 to-pink-500',
      features: [
        'Targeted audience reach',
        'Viral content optimization',
        'Engagement rate improvement',
        'Hashtag strategy development',
        'Influencer collaboration setup',
        'Real-time performance tracking'
      ]
    },
    {
      icon: <MessageSquare className="w-16 h-16" />,
      title: 'SMS Campaigns',
      subtitle: 'Direct Communication Power',
      description: 'Connect with your audience instantly through targeted SMS marketing campaigns that drive action and conversions.',
      color: 'from-orange-500 to-red-500',
      features: [
        'Personalized message creation',
        'Audience segmentation',
        'Scheduled campaign delivery',
        'Two-way SMS communication',
        'Compliance management',
        'Detailed delivery reports'
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-white" data-testid="services-page">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <h1 
              className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6"
              style={{ fontFamily: 'Space Grotesk, sans-serif' }}
              data-testid="services-title"
            >
              Our <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">Services</span>
            </h1>
            <p className="text-lg text-gray-600">
              Comprehensive digital marketing solutions designed to accelerate your business growth
            </p>
          </div>
        </div>
      </section>

      {/* Services Detail Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-24">
            {services.map((service, index) => (
              <div 
                key={index} 
                data-testid={`service-detail-${index}`}
                className={`flex flex-col ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-12 items-center`}
              >
                {/* Content */}
                <div className="flex-1">
                  <div className={`inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br ${service.color} rounded-2xl text-white mb-6`}>
                    {service.icon}
                  </div>
                  
                  <h2 
                    className="text-3xl sm:text-4xl font-bold mb-2 text-gray-900"
                    style={{ fontFamily: 'Space Grotesk, sans-serif' }}
                  >
                    {service.title}
                  </h2>
                  <p className="text-lg text-gray-500 mb-4">{service.subtitle}</p>
                  <p className="text-gray-600 text-lg mb-8 leading-relaxed">
                    {service.description}
                  </p>
                  
                  <div className="space-y-3 mb-8">
                    {service.features.map((feature, fIndex) => (
                      <div key={fIndex} className="flex items-start gap-3">
                        <div className={`flex-shrink-0 w-6 h-6 rounded-full bg-gradient-to-br ${service.color} flex items-center justify-center mt-0.5`}>
                          <Check className="w-4 h-4 text-white" />
                        </div>
                        <span className="text-gray-700">{feature}</span>
                      </div>
                    ))}
                  </div>
                  
                  <Link
                    to="/contact"
                    data-testid={`service-cta-${index}`}
                    className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg font-semibold hover:shadow-xl hover:scale-105 transition-all duration-300"
                  >
                    Get Started
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Link>
                </div>
                
                {/* Visual */}
                <div className="flex-1 w-full">
                  <div className={`bg-gradient-to-br ${service.color} rounded-3xl p-12 shadow-2xl transform hover:scale-105 transition-transform duration-300`}>
                    <div className="text-white text-center">
                      <div className="mb-8">
                        {React.cloneElement(service.icon, { className: 'w-32 h-32 mx-auto opacity-80' })}
                      </div>
                      <h3 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                        {service.title}
                      </h3>
                      <p className="text-white/90">
                        Proven strategies that deliver measurable results for your business
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50" data-testid="process-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 
              className="text-3xl sm:text-4xl font-bold mb-4"
              style={{ fontFamily: 'Space Grotesk, sans-serif' }}
            >
              Our <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">Process</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              A streamlined approach to digital marketing success
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            {[
              { step: '01', title: 'Consultation', desc: 'Understanding your goals and challenges' },
              { step: '02', title: 'Strategy', desc: 'Crafting a customized marketing plan' },
              { step: '03', title: 'Execution', desc: 'Launching campaigns with precision' },
              { step: '04', title: 'Optimization', desc: 'Continuous improvement for better results' }
            ].map((item, index) => (
              <div key={index} className="bg-white rounded-xl p-6 shadow-md hover:shadow-xl transition-shadow duration-300" data-testid={`process-step-${index}`}>
                <div className="text-5xl font-bold text-transparent bg-gradient-to-br from-blue-600 to-purple-600 bg-clip-text mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  {item.step}
                </div>
                <h3 className="text-xl font-bold mb-2 text-gray-900">{item.title}</h3>
                <p className="text-gray-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-white" data-testid="services-cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 
            className="text-3xl sm:text-4xl font-bold mb-6"
            style={{ fontFamily: 'Space Grotesk, sans-serif' }}
          >
            Ready to <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">Transform</span> Your Marketing?
          </h2>
          <p className="text-lg text-gray-600 mb-8">
            Let's discuss how our services can help you achieve your business goals
          </p>
          <Link
            to="/contact"
            data-testid="services-final-cta"
            className="inline-block px-10 py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl font-bold text-lg shadow-xl hover:shadow-2xl hover:scale-105 transition-all duration-300"
          >
            Schedule Free Consultation
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Services;